import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import Button from "../ui/Button";

export default function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = "md",
  showCloseButton = true,
}) {
  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    "2xl": "max-w-6xl",
    "3xl": "max-w-7xl",
    full: "max-w-full mx-4",
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        {/* Modal container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`w-full ${sizeClasses[size]} transform rounded-2xl bg-white text-left align-middle shadow-xl transition-all max-h-[90vh] flex flex-col`}
              >
                {/* Header */}
                {(title || subtitle || showCloseButton) && (
                  <div className="flex items-start justify-between px-6 pt-6 pb-4 flex-shrink-0">
                    <div>
                      {title && (
                        <Dialog.Title
                          as="h3"
                          className="text-xl font-semibold text-smiles-darkGray"
                        >
                          {title}
                        </Dialog.Title>
                      )}
                      {subtitle && (
                        <p className="mt-0.5 text-sm text-palette-gray-secondary">{subtitle}</p>
                      )}
                    </div>
                    {showCloseButton && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="text-smiles-lightGray hover:text-smiles-gray"
                        aria-label="Close"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className="w-6 h-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </Button>
                    )}
                  </div>
                )}

                {/* Content */}
                <div className="px-6 pb-6 overflow-y-auto flex-grow">{children}</div>

                {/* Footer */}
                {footer && (
                  <div className="px-6 pb-6 pt-4 flex-shrink-0 border-t border-palette-gray-subtle">
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                      {footer}
                    </div>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
